import { useMsal } from "@azure/msal-react";
import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Option } from "react-bootstrap-typeahead/types/types";
import {
    IDocFilter,
    getAllPeriods,
    getListDocuments,
} from "../../service/Docente/HistoryTachService";
import GenericUtils from "../../utils/GenericUtils";

type TachHistoryProps = {
    children: ReactNode;
};
type DateRange = [Date, Date];

type DocumentType = {
    id: number;
    nome: string;
    vigencia: string;
    docente: {
        id: number | string;
    };
};

type TachHistoryContextData = {
    years: number[];
    setYears: (years: number[]) => void;
    yearValue: Option[];
    setYearValue: (yearValue: Option[]) => void;
    dateRange: DateRange;
    setDateRange: (dateRange: DateRange) => void;
    teachers: Option[];
    setTeachers: (teachers: Option[]) => void;
    selectedTeacher: Option[];
    setSelectedTeacher: (teachers: Option[]) => void;
    handleAutoCompleteChange: (selected: Option[], canEdit: boolean) => void;
    documents: DocumentType[];
    setDocuments: (documents: DocumentType[]) => void;
    filterDocuments: (year: string, period: string) => void;
    getTachDocuments: (data: IDocFilter) => void;
    getDataYear: () => void;
    pagination: Pagination;
    setPagination: (pagination: Pagination) => void;
};

type Pagination = {
    actualPage: number;
    sizePage: number;
    totalPages: number;
    totalRecords: number;
};

const PAGE_SIZE = 100000000;

const TachHistoryContext = createContext<TachHistoryContextData>(
    {} as TachHistoryContextData
);

const TachHistoryProvider = ({ children }: TachHistoryProps) => {
    const { instance } = useMsal();
    const [pagination, setPagination] = useState<Pagination>({
        actualPage: 1,
        sizePage: PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const [teachers, setTeachers] = useState<Option[]>([]);
    const [selectedTeacher, setSelectedTeacher] = useState<Option[]>([
        {
            label: "",
            value: "",
        },
    ]);
    const [documents, setDocuments] = useState<DocumentType[]>([]);
    const [years, setYears] = useState<number[]>([]);
    const [yearValue, setYearValue] = useState<Option[]>([]);
    const [dateRange, setDateRange] = useState<DateRange>([
        new Date(),
        new Date(),
    ]);

    const handleAutoCompleteChange = (selected: Option[], canEdit: boolean) => {
        if (!canEdit) {
            return;
        }

        setSelectedTeacher(selected);
    };

    const filterDocuments = (period: string, year: string) => {
        const filteredDocuments = documents.filter(
            (document) =>
                document.vigencia.includes(year) ||
                document.vigencia.includes(period)
        );

        setDocuments(filteredDocuments);
    };

    const getTachDocuments = async (filteredData: IDocFilter) => {
        const response = await getListDocuments(instance, filteredData);
        if (GenericUtils.isSuccessStatus(response)) {
            const items = response.data.items as DocumentType[];
            setDocuments(items);
        }
    };

    const getDataYear = async () => {
        if (
            selectedTeacher &&
            selectedTeacher[0] &&
            (selectedTeacher[0] as any).value
        ) {
            try {
                const response = await getAllPeriods(
                    instance,
                    (selectedTeacher[0] as any).value
                );

                if (GenericUtils.isSuccessStatus(response)) {
                    const path = response.data.vigenciaAnos;
                    const data = path.map((period: any) => period.ano);
                    setYears(data);
                } else {
                    setYears([]);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Ocorreu um erro ao buscar os períodos:", error);
            }
        }
    };

    useEffect(() => {
        getDataYear();
    }, [selectedTeacher, yearValue]);

    const memoizedValue = useMemo(
        () => ({
            years,
            setYears,
            yearValue,
            setYearValue,
            dateRange,
            setDateRange,
            teachers,
            setTeachers,
            selectedTeacher,
            setSelectedTeacher,
            handleAutoCompleteChange,
            documents,
            setDocuments,
            filterDocuments,
            getTachDocuments,
            getDataYear,
            pagination,
            setPagination,
        }),
        [teachers, selectedTeacher, documents, years, pagination, dateRange]
    );

    return (
        <TachHistoryContext.Provider value={memoizedValue}>
            {children}
        </TachHistoryContext.Provider>
    );
};

const useTachHistory = () => {
    const context = useContext(TachHistoryContext);

    if (!context) {
        throw new Error("useTachHistory must be used within a TachHistory");
    }

    return context;
};

TachHistoryContext.displayName = "TachHistory";

export { TachHistoryContext, TachHistoryProvider, useTachHistory };

export type { DocumentType };
